import React, { useState, useEffect } from 'react';
import { getDocs, collection, doc, updateDoc, query, where } from "firebase/firestore";
import { db } from '../firebase/config';
import { MapPin, Monitor, Smartphone, Grid3x3, ChevronDown, ChevronRight, ChevronLeft, X, Search } from 'lucide-react';
import { SimpleHorseDetailsModal } from '../components/SimpleHorseModal';
import { FaListUl } from "react-icons/fa6";
import { PiMonitorBold } from "react-icons/pi";
import { FaWrench, FaHandHoldingMedical } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../layout/PageWrapper';

// Fonction utilitaire pour calculer l'âge
const getAge = (dateNaissance) => {
  if (!dateNaissance) return null;
  const today = new Date();
  const birthDate = new Date(dateNaissance);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// Utilitaires pour les couleurs de tags
const isHex = (s) => typeof s === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s);

const getTagColor = (tagId, tagColors) => {
  if (!tagId) return '#cccccc';
  if (isHex(tagId)) return tagId;
  const found = tagColors.find(tc => tc.id === parseInt(tagId));
  return found ? found.codeHex : '#cccccc';
};

// Fonction pour obtenir la couleur de fond selon surveillanceEtat
const getSurveillanceBackgroundColor = (horse) => {
  const etat = horse.surveillanceEtat || horse.SurveillanceEtat;
  if (!etat) return '';
  
  const backgrounds = {
    'vert': 'bg-green-50 border-l-2 border-green-500',
    'jaune': 'bg-yellow-50 border-l-2 border-yellow-500', 
    'orange': 'bg-orange-50 border-l-2 border-orange-500',
    'rouge': 'bg-red-50 border-l-2 border-red-500'
  };
  
  return backgrounds[etat.toLowerCase()] || '';
};

// Fonction pour obtenir la couleur selon la priorité
const getPriorityColor = (priority) => {
  if (priority === 'haute') return '#ef4444'; // rouge
  if (priority === 'moyenne') return '#f97316'; // orange
  return '#22c55e'; // vert
};

// Fonction pour formater le nombre avec singulier/pluriel
const formatHorseCount = (count) => {
  if (count === 0) return '0 cheval';
  if (count === 1) return '1 cheval';
  return `${count} chevaux`;
};

// Fonction pour calculer le nombre total de chevaux dans une zone et ses enfants
const getTotalHorsesInZone = (zone, horses, allZones) => {
  let total = 0;
  
  // Chevaux directement dans cette zone (seulement si la zone n'est pas virtuelle et peut accueillir des chevaux)
  if (!zone.isVirtual && zone.isOccupable !== false) {
    total += horses.filter(h => h.zoneId === zone.id).length;
  }
  
  // Fonction récursive pour compter dans les enfants
  const countInChildren = (parentId) => {
    const children = allZones.filter(z => z.parentId === parentId);
    children.forEach(child => {
      if (!child.isVirtual && child.isOccupable !== false) {
        total += horses.filter(h => h.zoneId === child.id).length;
      }
      countInChildren(child.id);
    });
  };
  
  countInChildren(zone.id);
  return total;
};

// COMPOSANT PRINCIPAL
export default function ListeChevauxPage() {
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [zones, setZones] = useState([]);
  const [tagColors, setTagColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [showHorseDetails, setShowHorseDetails] = useState(false);
  
  // VÉRIFICATION DU MODE ICI - récupéré depuis l'URL et localStorage
  const isTV = window.location.pathname === '/liste-chevaux-tv';
  const savedMode = localStorage.getItem('listeChevauxViewMode');
  const [viewMode, setViewMode] = useState(isTV ? 'tv' : (savedMode || 'web'));
  
  const [expandedZones, setExpandedZones] = useState({});
  const [technicalTickets, setTechnicalTickets] = useState([]);
  const [medicalTickets, setMedicalTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);

  // Sauvegarder le mode dans localStorage quand il change
  useEffect(() => {
    localStorage.setItem('listeChevauxViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    loadData();
    fetchTagColors();
  }, []);

  const fetchTagColors = async () => {
    try {
      const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
      let colorsData = [];
      
      tagColorsSnap.forEach(doc => {
        const data = doc.data();
        if (data.id && data.codeHex) {
          colorsData.push({ 
            id: data.id,
            nom: data.nom || '',
            codeHex: data.codeHex,
            ordre: data.ordre || data.id
          });
        }
      });

      colorsData.sort((a, b) => a.ordre - b.ordre);
      setTagColors(colorsData);
    } catch (error) {
      console.error('Erreur lors du chargement des couleurs:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les chevaux
      const chevauxSnap = await getDocs(collection(db, "animaux"));
      let horsesData = [];
      chevauxSnap.forEach(doc => {
        const d = doc.data();
        horsesData.push({ id: doc.id, ...d });
      });

      // Charger les zones
      const zonesSnap = await getDocs(collection(db, "lieux"));
      let zonesData = [];
      zonesSnap.forEach(doc => {
        const d = doc.data();
        zonesData.push({ 
          id: doc.id, 
          ...d,
          level: d.level ?? 0,
          order: d.order ?? 999,
          parentId: d.parentId ?? null,
          isOccupable: d.isOccupable ?? true,
          isVirtual: d.isVirtual ?? false
        });
      });
      
      // Trier les zones
      zonesData.sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level;
        return a.order - b.order;
      });

      // Charger les tickets techniques
      try {
        const technicalQuery = query(
          collection(db, "tickets"),
          where("statut", "==", "ouvert"),
          where("type", "==", "technique")
        );
        const technicalSnap = await getDocs(technicalQuery);
        const technicalData = [];
        technicalSnap.forEach(doc => {
          const data = doc.data();
          if (data.zoneId || data.zoneName) {
            technicalData.push({ id: doc.id, ...data });
          }
        });
        setTechnicalTickets(technicalData);
      } catch (error) {
        console.error('Erreur chargement tickets techniques:', error);
      }

      // Charger les tickets médicaux
      try {
        const medicalQuery = query(
          collection(db, "tickets"),
          where("statut", "==", "ouvert"),
          where("type", "==", "medical")
        );
        const medicalSnap = await getDocs(medicalQuery);
        const medicalData = [];
        medicalSnap.forEach(doc => {
          const data = doc.data();
          medicalData.push({ id: doc.id, ...data });
        });
        setMedicalTickets(medicalData);
      } catch (error) {
        console.error('Erreur chargement tickets médicaux:', error);
      }

      setHorses(horsesData);
      setZones(zonesData);
      
      // Initialiser toutes les zones comme dépliées
      const initialExpanded = {};
      zonesData.forEach(zone => {
        initialExpanded[zone.id] = true;
      });
      setExpandedZones(initialExpanded);
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    }
  };

  // Construire l'arbre hiérarchique
  const buildZoneTree = () => {
    const tree = [];
    const zoneMap = {};
    
    zones.forEach(zone => {
      zoneMap[zone.id] = { ...zone, children: [] };
    });
    
    zones.forEach(zone => {
      if (zone.parentId && zoneMap[zone.parentId]) {
        zoneMap[zone.parentId].children.push(zoneMap[zone.id]);
      } else if (!zone.parentId) {
        tree.push(zoneMap[zone.id]);
      }
    });
    
    return tree;
  };

  // Obtenir les tickets techniques pour une zone
  const getZoneTechnicalTickets = (zoneId) => {
    return technicalTickets.filter(t => t.zoneId === zoneId);
  };

  // Obtenir les tickets médicaux pour un cheval
  const getHorseMedicalTickets = (horseId, horseName) => {
    return medicalTickets.filter(t => 
      t.horseId === horseId || t.horseName === horseName
    );
  };

  const handleHorseClick = (horse) => {
    if (viewMode === 'tv') return; // Pas de clic en mode TV
    
    const horseWithAge = {
      ...horse,
      age: getAge(horse.dateNaissance)
    };
    setSelectedHorse(horseWithAge);
    setShowHorseDetails(true);
  };

  const handleMoveHorse = async (targetZoneId) => {
    if (!selectedHorse || !targetZoneId) return;

    try {
      await updateDoc(doc(db, "animaux", selectedHorse.id), {
        zoneId: targetZoneId
      });
      
      await loadData();
      setShowHorseDetails(false);
      setSelectedHorse(null);
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
    }
  };

  const toggleZone = (zoneId) => {
    if (viewMode === 'tv') return; // Pas de repli en mode TV
    setExpandedZones(prev => ({
      ...prev,
      [zoneId]: !prev[zoneId]
    }));
  };

  // Fonction de recherche de cheval
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    // Trouver TOUS les chevaux correspondants
    const foundHorses = horses.filter(h => 
      h.nom.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (foundHorses.length > 0) {
      setSearchResults(foundHorses);
      setCurrentSearchIndex(0);
      navigateToHorse(foundHorses[0]);
    } else {
      setSearchResults([]);
      alert('Aucun cheval trouvé');
    }
  };

  // Naviguer vers un cheval spécifique
  const navigateToHorse = (horse) => {
    // Trouver et déplier toutes les zones parentes
    const horseZone = zones.find(z => z.id === horse.zoneId);
    if (horseZone) {
      const zonesToExpand = {};
      
      // Fonction récursive pour déplier tous les parents
      const expandParents = (zone) => {
        zonesToExpand[zone.id] = true;
        if (zone.parentId) {
          const parent = zones.find(z => z.id === zone.parentId);
          if (parent) expandParents(parent);
        }
      };
      
      expandParents(horseZone);
      
      // Déplier toutes les zones nécessaires
      setExpandedZones(prev => ({
        ...prev,
        ...zonesToExpand
      }));

      // Faire défiler jusqu'au cheval après un court délai
      setTimeout(() => {
        const element = document.getElementById(`horse-${horse.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Naviguer vers le résultat suivant
  const goToNextResult = () => {
    if (searchResults.length > 0) {
      const nextIndex = (currentSearchIndex + 1) % searchResults.length;
      setCurrentSearchIndex(nextIndex);
      navigateToHorse(searchResults[nextIndex]);
    }
  };

  // Naviguer vers le résultat précédent
  const goToPreviousResult = () => {
    if (searchResults.length > 0) {
      const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
      setCurrentSearchIndex(prevIndex);
      navigateToHorse(searchResults[prevIndex]);
    }
  };

  // Réinitialiser la recherche
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
  };

  // Fonction pour formater l'affichage du nombre de chevaux
  const formatZoneHorseCount = (zone, directCount, totalCount) => {
    const hasChildren = zone.children && zone.children.length > 0;
    
    if (zone.isVirtual) {
      // Zone virtuelle : juste le total
      return formatHorseCount(totalCount);
    } else if (!zone.isOccupable && hasChildren) {
      // Zone qui ne peut pas recevoir de chevaux mais a des enfants
      return `0 | ${totalCount}`;
    } else if (hasChildren) {
      // Zone avec enfants : direct | total
      return `${directCount} | ${totalCount}`;
    } else {
      // Zone sans enfants : juste le nombre direct
      return formatHorseCount(directCount);
    }
  };

  // Composant pour afficher une zone et ses enfants en mode TV (ultra compact)
  const ZoneBlockTV = ({ zone, level = 0 }) => {
    const horsesInZone = horses.filter(h => h.zoneId === zone.id);
    const directCount = horsesInZone.length;
    const totalCount = getTotalHorsesInZone(zone, horses, zones);
    const zoneTickets = getZoneTechnicalTickets(zone.id);
    const hasChildren = zone.children && zone.children.length > 0;

    return (
      <div 
        className={`
          ${zone.isVirtual ? 'border border-dashed border-gray-300' : 'border border-solid border-gray-200'}
          ${level > 0 ? 'ml-1' : ''}
          mb-0.5
          rounded
          ${zone.isVirtual ? 'bg-gray-50' : 'bg-white'}
          text-[10px]
        `}
      >
        {/* En-tête de la zone - ultra compact */}
        <div className={`
          px-1 py-0.5 
          ${zone.isVirtual ? 'bg-gray-100' : 'bg-gray-50'} 
          border-b border-gray-200
          flex items-center gap-1
        `}>
          <div 
            className="w-2 h-2 rounded-full flex-shrink-0" 
            style={{ backgroundColor: zone.isVirtual ? 'transparent' : zone.color }}
          />
          <span className="font-semibold truncate">{zone.name}</span>
          <span className="text-gray-500">({formatZoneHorseCount(zone, directCount, totalCount)})</span>
          
          {/* Tickets techniques */}
          {zoneTickets.map((ticket, idx) => (
            <FaWrench 
              key={idx}
              className="h-2 w-2 flex-shrink-0"
              style={{ color: getPriorityColor(ticket.priority) }}
            />
          ))}
        </div>

        {/* Chevaux - affichage inline ultra compact */}
        {horsesInZone.length > 0 && !zone.isVirtual && (
          <div className="px-1 py-0.5 flex flex-wrap gap-0.5">
            {horsesInZone.map(horse => {
              const medicalTickets = getHorseMedicalTickets(horse.id, horse.nom);
              
              return (
                <div
                  key={horse.id}
                  className={`
                    inline-flex items-center gap-0.5 px-1 rounded
                    ${getSurveillanceBackgroundColor(horse)}
                  `}
                >
                  <div 
                    className="w-1.5 h-1.5 rounded-full border border-gray-300"
                    style={{ backgroundColor: getTagColor(horse.tag, tagColors) }}
                  />
                  <span className="font-medium">{horse.nom}</span>
                  {medicalTickets.map((ticket, idx) => (
                    <FaHandHoldingMedical
                      key={idx}
                      className="h-2 w-2"
                      style={{ color: getPriorityColor(ticket.priority) }}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Zones enfants */}
        {hasChildren && (
          <div className="px-0.5">
            {zone.children.map(child => (
              <ZoneBlockTV key={child.id} zone={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Composant pour afficher une zone et ses enfants en mode Web (normal)
  const ZoneBlockWeb = ({ zone, level = 0 }) => {
    const horsesInZone = horses.filter(h => h.zoneId === zone.id);
    const directCount = horsesInZone.length;
    const totalCount = getTotalHorsesInZone(zone, horses, zones);
    const zoneTickets = getZoneTechnicalTickets(zone.id);
    const isExpanded = expandedZones[zone.id] !== false;
    const hasChildren = zone.children && zone.children.length > 0;

    return (
      <div 
        className={`
          ${zone.isVirtual ? 'border-2 border-dashed border-gray-400' : 'border border-solid border-gray-300'}
          ${level > 0 ? 'ml-4' : ''}
          mb-4
          rounded-lg
          ${zone.isVirtual ? 'bg-gray-50' : 'bg-white'}
        `}
      >
        {/* En-tête de la zone */}
        <div 
          className={`
            px-4 py-3 
            ${zone.isVirtual ? 'bg-gray-100' : 'bg-gray-50'} 
            border-b 
            ${hasChildren || horsesInZone.length > 0 ? 'cursor-pointer hover:bg-gray-200' : ''}
            flex items-center justify-between
          `}
          onClick={() => toggleZone(zone.id)}
        >
          <div className="flex items-center gap-2">
            {(hasChildren || horsesInZone.length > 0) && (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            )}
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: zone.isVirtual ? 'transparent' : zone.color }}
            />
            <h3 className="text-lg font-semibold">
              {zone.name}
            </h3>
            <span className="text-sm text-gray-500">
              ({zone.type}) - {formatZoneHorseCount(zone, directCount, totalCount)}
            </span>
            
            {/* Tickets techniques de la zone */}
            {zoneTickets.map((ticket, idx) => (
              <FaWrench 
                key={idx}
                className="h-4 w-4 cursor-pointer hover:scale-110"
                style={{ color: getPriorityColor(ticket.priority) }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/tableau-bord', { 
                    state: { 
                      zoneId: zone.id,
                      zoneName: zone.name,
                      filter: 'technique'
                    }
                  });
                }}
                title={`Ticket technique: ${ticket.title} - Priorité ${ticket.priority}`}
              />
            ))}
          </div>
        </div>

        {/* Contenu de la zone (chevaux et sous-zones) */}
        {isExpanded && (
          <div className={`${zone.isVirtual ? '' : 'p-3'}`}>
            {/* Liste des chevaux */}
            {horsesInZone.length > 0 && !zone.isVirtual && (
              <div className="space-y-2 mb-3">
                {horsesInZone.map(horse => {
                  const medicalTickets = getHorseMedicalTickets(horse.id, horse.nom);
                  
                  return (
                    <div
                      key={horse.id}
                      id={`horse-${horse.id}`}
                      className={`
                        px-4 py-3
                        hover:bg-gray-50 cursor-pointer
                        rounded flex items-center justify-between
                        ${getSurveillanceBackgroundColor(horse)}
                        ${searchResults.length > 0 && searchResults[currentSearchIndex]?.id === horse.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                      `}
                      onClick={() => handleHorseClick(horse)}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full border border-gray-300"
                          style={{ backgroundColor: getTagColor(horse.tag, tagColors) }}
                        />
                        <span className="text-sm font-medium">
                          {horse.nom}
                        </span>
                        <span className="text-sm text-gray-500">
                          {getAge(horse.dateNaissance) ? `${getAge(horse.dateNaissance)} ans` : ''}
                        </span>
                        <span className="text-sm text-gray-500">{horse.sexe}</span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {medicalTickets.map((ticket, idx) => (
                          <FaHandHoldingMedical
                            key={idx}
                            className="h-4 w-4 cursor-pointer hover:scale-110"
                            style={{ color: getPriorityColor(ticket.priority) }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const horseId = horses.find(h => h.nom === horse.nom)?.id;
                              if (horseId) {
                                navigate('/tableau-bord', { 
                                  state: { 
                                    horseId: horseId,
                                    filter: 'medical'
                                  }
                                });
                              } else {
                                navigate('/tableau-bord');
                              }
                            }}
                            title={`Ticket médical: ${ticket.title} - Priorité ${ticket.priority}`}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Zones enfants */}
            {hasChildren && (
              <div className={zone.isVirtual ? '' : 'space-y-2'}>
                {zone.children.map(child => (
                  <ZoneBlockWeb key={child.id} zone={child} level={level + 1} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ========== RENDU CONDITIONNEL DIRECT ==========
  
  // Si on est en mode TV, on retourne DIRECTEMENT le JSX (pas de PageWrapper)
  if (viewMode === 'tv') {
    // Calculer le nombre de colonnes
    const zoneTree = buildZoneTree();
    const totalHorses = horses.length;
    let numColumns = 2;
    if (totalHorses > 40) numColumns = 3;
    if (totalHorses > 80) numColumns = 4;
    if (totalHorses > 120) numColumns = 5;
    if (totalHorses > 160) numColumns = 6;

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header TV ultra compact SANS menu */}
        <div className="bg-white shadow-sm border-b px-2 py-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src="/logo.svg" 
                alt="HIPOO" 
                className="h-6 w-auto"
              />
              <h1 className="text-sm font-bold text-gray-900">Liste des Chevaux</h1>
              <span className="text-sm text-gray-600">- Total de {horses.length} {horses.length === 1 ? 'cheval' : 'chevaux'}</span>
              {loading && <span className="text-xs text-gray-500 ml-2">Chargement...</span>}
            </div>
            <button
              onClick={() => {
                setViewMode('web');
                navigate('/liste-chevaux');
              }}
              className="flex items-center gap-1 px-2 py-0.5 border border-gray-300 rounded text-xs hover:bg-gray-50"
            >
              <Smartphone className="h-3 w-3" />
              Vue Web
            </button>
          </div>
        </div>

        {/* Contenu TV - Grille adaptative */}
        <div className="p-1">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-lg">Chargement...</div>
            </div>
          ) : (
            <div 
              className="gap-1"
              style={{ 
                columnCount: numColumns,
                columnGap: '0.25rem'
              }}
            >
              {zoneTree.map(zone => (
                <div key={zone.id} className="break-inside-avoid mb-1">
                  <ZoneBlockTV zone={zone} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // MODE WEB - Avec PageWrapper et menu
  const zoneTree = buildZoneTree();
  
  return (
    <PageWrapper icon={FaListUl} title="Liste des Chevaux" maxWidth="w-full">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      ) : (
        <div className="w-full">
          <div className="flex items-center justify-between mb-6 gap-4">
            <span className="text-lg text-gray-500">Total : {formatHorseCount(horses.length)}</span>
            
            <div className="flex items-center gap-3">
              {/* Champ de recherche avec boutons de navigation */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un cheval..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <button
                  onClick={handleSearch}
                  className="p-2 bg-black text-white rounded-md hover:bg-gray-800"
                  title="Rechercher"
                >
                  <Search className="h-4 w-4" />
                </button>
                
                {searchResults.length > 1 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md">
                    <button
                      onClick={goToPreviousResult}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="Résultat précédent"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600 px-2">
                      {currentSearchIndex + 1} / {searchResults.length}
                    </span>
                    <button
                      onClick={goToNextResult}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="Résultat suivant"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
                
                {searchResults.length === 1 && (
                  <span className="text-sm text-green-600">
                    1 cheval trouvé
                  </span>
                )}
              </div>
              
              <button
                onClick={() => {
                  setViewMode('tv');
                  navigate('/liste-chevaux-tv');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
              >
                <PiMonitorBold className="h-4 w-4" />
                Mode TV
              </button>
            </div>
          </div>

          {/* Contenu principal Web */}
          <div className="space-y-4 w-full">
            {zoneTree.map(zone => (
              <ZoneBlockWeb key={zone.id} zone={zone} />
            ))}
          </div>

          {/* Modal de détails du cheval */}
          {showHorseDetails && selectedHorse && (
            <SimpleHorseDetailsModal
              isOpen={showHorseDetails}
              onClose={() => {
                setShowHorseDetails(false);
                setSelectedHorse(null);
              }}
              horse={selectedHorse}
              zones={zones}
              onMoveHorse={handleMoveHorse}
              onStartMoveFromMap={() => {}}
            />
          )}
        </div>
      )}
    </PageWrapper>
  );
};